// This file provides a client-side API for accessing database data
// In a production app, this would make HTTP requests to a backend server
// For now, we'll use localStorage as a simple data store that mirrors the database structure

import type { 
  HeroSlide, 
  WelcomeSection, 
  BookSection, 
  GalleryImage, 
  LocationFeature,
  LocationSection,
  GallerySection 
} from './db-api';

// Initialize default data
const initializeData = () => {
  if (!localStorage.getItem('heroSlides')) {
    const defaultHeroSlides: HeroSlide[] = [
      {
        id: 1,
        image: '/src/assets/hero-slide-1.jpg',
        title: 'Luxury Villas in Lindos',
        subtitle: 'Experience the Magic of Rhodes Island',
        order_index: 1,
        active: 1
      },
      {
        id: 2,
        image: '/src/assets/hero-slide-2.jpg',
        title: 'Breathtaking Views',
        subtitle: 'Where Ancient History Meets Modern Comfort',
        order_index: 2,
        active: 1
      },
      {
        id: 3,
        image: '/src/assets/hero-slide-3.jpg',
        title: 'Your Mediterranean Dream',
        subtitle: 'Unforgettable Moments Await',
        order_index: 3,
        active: 1
      }
    ];
    localStorage.setItem('heroSlides', JSON.stringify(defaultHeroSlides));
  }

  if (!localStorage.getItem('welcomeSection')) {
    const defaultWelcome: WelcomeSection = {
      id: 1,
      title: 'Welcome to Paradise',
      paragraph1: 'Discover two exquisite villas nestled in the heart of Lindos, one of the most picturesque villages on Rhodes Island. Our luxury accommodations offer the perfect blend of traditional Greek charm and modern comfort.',
      paragraph2: 'Wake up to breathtaking views of the Aegean Sea, explore ancient ruins just steps away, and experience the warm hospitality that Greece is famous for. Your unforgettable Mediterranean escape awaits.'
    };
    localStorage.setItem('welcomeSection', JSON.stringify(defaultWelcome));
  }

  if (!localStorage.getItem('bookSection')) {
    const defaultBook: BookSection = {
      id: 1,
      title: 'Ready for Your Greek Getaway?',
      description: 'Book your stay at our luxury Lindos villas and create memories that will last a lifetime',
      email: 'info@lindosvillas.com',
      phone: '+30 123 456 7890'
    };
    localStorage.setItem('bookSection', JSON.stringify(defaultBook));
  }

  if (!localStorage.getItem('galleryImages')) {
    const defaultGallery: GalleryImage[] = Array.from({ length: 6 }, (_, i) => ({
      id: i + 1,
      image: `/src/assets/gallery-${i + 1}.jpg`,
      alt_text: `Gallery image ${i + 1}`,
      order_index: i + 1,
      active: 1
    }));
    localStorage.setItem('galleryImages', JSON.stringify(defaultGallery));
  }

  if (!localStorage.getItem('locationFeatures')) {
    const defaultFeatures: LocationFeature[] = [
      {
        id: 1,
        icon: 'MapPin',
        title: 'Prime Location',
        description: 'Located in the heart of Lindos village',
        order_index: 1,
        active: 1
      },
      {
        id: 2,
        icon: 'Landmark',
        title: 'Historic Site',
        description: 'Walking distance to the ancient Acropolis',
        order_index: 2,
        active: 1
      },
      {
        id: 3,
        icon: 'Waves',
        title: 'Beach Access',
        description: 'Minutes away from pristine beaches',
        order_index: 3,
        active: 1
      },
      {
        id: 4,
        icon: 'Sun',
        title: 'Year-Round Sun',
        description: '300+ days of sunshine annually',
        order_index: 4,
        active: 1
      }
    ];
    localStorage.setItem('locationFeatures', JSON.stringify(defaultFeatures));
  }

  if (!localStorage.getItem('locationSection')) {
    const defaultLocation: LocationSection = {
      id: 1,
      title: 'Discover Lindos',
      description: 'Lindos is a jewel of Rhodes, combining ancient history with modern luxury. Our villas put you at the center of this magical destination.'
    };
    localStorage.setItem('locationSection', JSON.stringify(defaultLocation));
  }

  if (!localStorage.getItem('gallerySection')) {
    const defaultGallerySection: GallerySection = {
      id: 1,
      title: 'Gallery',
      description: 'Take a visual journey through our stunning villas and the beauty of Lindos'
    };
    localStorage.setItem('gallerySection', JSON.stringify(defaultGallerySection));
  }
};

// Initialize on import
initializeData();

// Event system for real-time updates
const dispatchUpdateEvent = (type: string) => {
  window.dispatchEvent(new CustomEvent('contentUpdated', { detail: { type } }));
};

// Client API functions
export const clientAPI = {
  // Hero Slides
  getHeroSlides: (): HeroSlide[] => {
    const data = localStorage.getItem('heroSlides');
    return data ? JSON.parse(data).filter((s: HeroSlide) => s.active === 1) : [];
  },

  createHeroSlide: (slide: Omit<HeroSlide, 'id'>): void => {
    const slides = JSON.parse(localStorage.getItem('heroSlides') || '[]');
    const newSlide = { ...slide, id: Date.now() };
    slides.push(newSlide);
    localStorage.setItem('heroSlides', JSON.stringify(slides));
    dispatchUpdateEvent('heroSlides');
  },

  updateHeroSlide: (id: number, slide: Partial<HeroSlide>): void => {
    const slides = JSON.parse(localStorage.getItem('heroSlides') || '[]');
    const index = slides.findIndex((s: HeroSlide) => s.id === id);
    if (index !== -1) {
      slides[index] = { ...slides[index], ...slide };
      localStorage.setItem('heroSlides', JSON.stringify(slides));
      dispatchUpdateEvent('heroSlides');
    }
  },

  deleteHeroSlide: (id: number): void => {
    const slides = JSON.parse(localStorage.getItem('heroSlides') || '[]');
    const filtered = slides.filter((s: HeroSlide) => s.id !== id);
    localStorage.setItem('heroSlides', JSON.stringify(filtered));
    dispatchUpdateEvent('heroSlides');
  },

  // Welcome Section
  getWelcomeSection: (): WelcomeSection | null => {
    const data = localStorage.getItem('welcomeSection');
    return data ? JSON.parse(data) : null;
  },

  updateWelcomeSection: (section: Partial<WelcomeSection>): void => {
    const current = JSON.parse(localStorage.getItem('welcomeSection') || '{}');
    localStorage.setItem('welcomeSection', JSON.stringify({ ...current, ...section }));
    dispatchUpdateEvent('welcomeSection');
  },

  // Book Section
  getBookSection: (): BookSection | null => {
    const data = localStorage.getItem('bookSection');
    return data ? JSON.parse(data) : null;
  },

  updateBookSection: (section: Partial<BookSection>): void => {
    const current = JSON.parse(localStorage.getItem('bookSection') || '{}');
    localStorage.setItem('bookSection', JSON.stringify({ ...current, ...section }));
    dispatchUpdateEvent('bookSection');
  },

  // Gallery Images
  getGalleryImages: (): GalleryImage[] => {
    const data = localStorage.getItem('galleryImages');
    return data ? JSON.parse(data).filter((img: GalleryImage) => img.active === 1) : [];
  },

  createGalleryImage: (image: Omit<GalleryImage, 'id'>): void => {
    const images = JSON.parse(localStorage.getItem('galleryImages') || '[]');
    const newImage = { ...image, id: Date.now() };
    images.push(newImage);
    localStorage.setItem('galleryImages', JSON.stringify(images));
    console.log('Created gallery image:', newImage);
    console.log('Total gallery images:', images.length);
    dispatchUpdateEvent('galleryImages');
  },

  updateGalleryImage: (id: number, image: Partial<GalleryImage>): void => {
    const images = JSON.parse(localStorage.getItem('galleryImages') || '[]');
    const index = images.findIndex((img: GalleryImage) => img.id === id);
    if (index !== -1) {
      images[index] = { ...images[index], ...image };
      localStorage.setItem('galleryImages', JSON.stringify(images));
      console.log('Updated gallery image:', images[index]);
      dispatchUpdateEvent('galleryImages');
    } else {
      console.error('Gallery image not found:', id);
    }
  },

  deleteGalleryImage: (id: number): void => {
    const images = JSON.parse(localStorage.getItem('galleryImages') || '[]');
    const filtered = images.filter((img: GalleryImage) => img.id !== id);
    localStorage.setItem('galleryImages', JSON.stringify(filtered));
    dispatchUpdateEvent('galleryImages');
  },

  // Location Features
  getLocationFeatures: (): LocationFeature[] => {
    const data = localStorage.getItem('locationFeatures');
    return data ? JSON.parse(data).filter((f: LocationFeature) => f.active === 1) : [];
  },

  createLocationFeature: (feature: Omit<LocationFeature, 'id'>): void => {
    const features = JSON.parse(localStorage.getItem('locationFeatures') || '[]');
    const newFeature = { ...feature, id: Date.now() };
    features.push(newFeature);
    localStorage.setItem('locationFeatures', JSON.stringify(features));
    dispatchUpdateEvent('locationFeatures');
  },

  updateLocationFeature: (id: number, feature: Partial<LocationFeature>): void => {
    const features = JSON.parse(localStorage.getItem('locationFeatures') || '[]');
    const index = features.findIndex((f: LocationFeature) => f.id === id);
    if (index !== -1) {
      features[index] = { ...features[index], ...feature };
      localStorage.setItem('locationFeatures', JSON.stringify(features));
      dispatchUpdateEvent('locationFeatures');
    }
  },

  deleteLocationFeature: (id: number): void => {
    const features = JSON.parse(localStorage.getItem('locationFeatures') || '[]');
    const filtered = features.filter((f: LocationFeature) => f.id !== id);
    localStorage.setItem('locationFeatures', JSON.stringify(filtered));
    dispatchUpdateEvent('locationFeatures');
  },

  // Location Section
  getLocationSection: (): LocationSection | null => {
    const data = localStorage.getItem('locationSection');
    return data ? JSON.parse(data) : null;
  },

  updateLocationSection: (section: Partial<LocationSection>): void => {
    const current = JSON.parse(localStorage.getItem('locationSection') || '{}');
    localStorage.setItem('locationSection', JSON.stringify({ ...current, ...section }));
    dispatchUpdateEvent('locationSection');
  },

  // Gallery Section
  getGallerySection: (): GallerySection | null => {
    const data = localStorage.getItem('gallerySection');
    return data ? JSON.parse(data) : null;
  },

  updateGallerySection: (section: Partial<GallerySection>): void => {
    const current = JSON.parse(localStorage.getItem('gallerySection') || '{}');
    localStorage.setItem('gallerySection', JSON.stringify({ ...current, ...section }));
    dispatchUpdateEvent('gallerySection');
  }
};

